import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CalendarPlus } from "lucide-react";
import { api, gbp, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export const SUBJECTS = [
  "Mathematics", "Physics", "Chemistry", "Biology", "Psychology",
  "English Literature", "English Language", "Geography", "Business", "Religious Studies",
];

export function BookingDialog({ children, tutors, onBooked }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ child_id: "", subject: "", level: "GCSE", plan: "", date: "", time: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const { data: catalog = [] } = useQuery({ queryKey: ["catalog"], queryFn: async () => (await api.get("/catalog")).data });
  const plans = catalog.filter((c) => c.level === f.level);

  const submit = async () => {
    if (!f.child_id || !f.subject || !f.plan || !f.date || !f.time) {
      toast.error("Please complete all fields");
      return;
    }
    setLoading(true);
    try {
      const scheduled_at = new Date(`${f.date}T${f.time}`).toISOString();
      const { data } = await api.post("/bookings", {
        child_id: f.child_id, subject: f.subject, plan: f.plan,
        scheduled_at, origin_url: window.location.origin,
      });
      toast.success("Redirecting to secure checkout...");
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-[#BA8A30] hover:bg-[#9C7328] text-[#2A1E0E] font-semibold btn-lift" data-testid="open-booking-btn">
          <CalendarPlus className="h-4 w-4 mr-2" /> Book a lesson
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#595C2B]">Book a lesson</DialogTitle>
          <DialogDescription>Choose a plan and time. You'll be taken to secure checkout.</DialogDescription>
        </DialogHeader>

        {children.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">Please add a child first before booking a lesson.</p>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">Child</Label>
              <Select value={f.child_id} onValueChange={(v) => set("child_id", v)}>
                <SelectTrigger className="mt-1" data-testid="booking-child-select"><SelectValue placeholder="Select child" /></SelectTrigger>
                <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.level}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Level</Label>
                <Select value={f.level} onValueChange={(v) => { set("level", v); set("plan", ""); }}>
                  <SelectTrigger className="mt-1" data-testid="booking-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="GCSE">GCSE</SelectItem><SelectItem value="A-Level">A-Level</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Subject</Label>
                <Select value={f.subject} onValueChange={(v) => set("subject", v)}>
                  <SelectTrigger className="mt-1" data-testid="booking-subject-select"><SelectValue placeholder="Subject" /></SelectTrigger>
                  <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Plan / Package</Label>
              <Select value={f.plan} onValueChange={(v) => set("plan", v)}>
                <SelectTrigger className="mt-1" data-testid="booking-plan-select"><SelectValue placeholder="Select a plan" /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.lookup_key} value={p.lookup_key}>{p.label} — {gbp(p.amount)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Date</Label>
                <Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="mt-1" data-testid="booking-date-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Time</Label>
                <Input type="time" value={f.time} onChange={(e) => set("time", e.target.value)} className="mt-1" data-testid="booking-time-input" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button onClick={submit} disabled={loading || children.length === 0} className="w-full rounded-full bg-[#595C2B] hover:bg-[#45481F] text-white btn-lift" data-testid="confirm-booking-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
